/**
 * Reports Dashboard View
 *
 * Displays:
 * - Summary statistics cards (total requests by status)
 * - Expenditure by type breakdown chart
 * - Expenditure by department chart
 * - Monthly/quarterly trends chart
 * - Pending approval aging table
 * - Date range selector for filtering
 */

import { renderIcon } from '../utils/icons';
import { createSidebarLayout } from '../components/sidebar-layout';
import { createDateRangePicker, type DateRange } from '../components/date-range-picker';
import { createSelect } from '../components/select';
import { apiClient } from '../services/api-client';
import {
  createDoughnutChart,
  createLineChart,
  createHorizontalBarChart,
  type ChartInstance,
} from '../components/chart';

/**
 * Summary statistics response from API
 */
interface SummaryStats {
  byStatus: {
    status: string;
    count: number;
    totalAmount: number;
  }[];
  totals: {
    totalRequests: number;
    totalAmount: number;
    averageAmount: number;
  };
}

/**
 * Expenditure by type response
 */
interface ExpenditureByType {
  type: string;
  count: number;
  totalAmount: number;
  percentageOfTotal: number;
}

/**
 * Expenditure by department response
 */
interface ExpenditureByDepartment {
  department: string;
  count: number;
  totalAmount: number;
  percentageOfTotal: number;
}

/**
 * Trend data point
 */
interface TrendDataPoint {
  period: string;
  count: number;
  totalAmount: number;
  approvedCount: number;
  approvedAmount: number;
  rejectedCount: number;
  rejectedAmount: number;
}

/**
 * Aging bucket data
 */
interface AgingBucket {
  bucket: string;
  minDays: number;
  maxDays: number | null;
  count: number;
  totalAmount: number;
  requests: Array<{
    id: string;
    title: string;
    amount: number;
    currency: string;
    submittedAt: string;
    daysAging: number;
    createdBy: string;
  }>;
}

/**
 * Aging report response
 */
interface AgingReport {
  buckets: AgingBucket[];
  summary: {
    totalPending: number;
    totalAmount: number;
    averageDaysAging: number;
    oldestRequest: number;
  };
}

/**
 * Approval benchmark data
 */
interface ApprovalBenchmark {
  dimension: string;
  dimensionValue: string;
  averageApprovalTime: number;
  medianApprovalTime: number;
  minApprovalTime: number;
  maxApprovalTime: number;
  totalRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  approvalRate: number;
}

/**
 * Spending prediction data
 */
interface SpendingPrediction {
  period: string;
  predictedAmount: number;
  actualAmount?: number;
  confidence: number;
  trendDirection: 'up' | 'down' | 'stable';
  percentageChange: number;
}

/**
 * Date range filter state
 */
interface DateRangeState {
  dateRange: DateRange;
}

/**
 * Track Chart.js instances for cleanup
 */
interface ChartInstances {
  byType?: ChartInstance;
  byDept?: ChartInstance;
  statusDistribution?: ChartInstance;
  trends?: ChartInstance;
  approvalRate?: ChartInstance;
}

/**
 * Status badge colors
 */
const statusColors: Record<string, { bg: string; text: string; icon: string }> = {
  PENDING: { bg: 'bg-warning/20', text: 'text-warning', icon: 'clock' },
  APPROVED: { bg: 'bg-success/20', text: 'text-success', icon: 'checkCircle' },
  REJECTED: { bg: 'bg-destructive/20', text: 'text-destructive', icon: 'xCircle' },
  CANCELLED: { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'slash' },
};

/**
 * Chart bar colors (dark theme optimized)
 */
/**
 * Format currency amount
 */
function formatAmount(amount: number, currency: string = 'CHF'): string {
  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format number with commas
 */
function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-CH').format(num);
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Create a stat card element
 */
function createStatCard(
  icon: string,
  iconColor: string,
  label: string,
  value: number | string,
  subtext?: string,
  subValue?: string
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'glass-sm p-5 rounded-xl transition-all hover:scale-[1.02] hover:shadow-lg';

  card.innerHTML = `
    <div class="flex items-center gap-4">
      <div class="w-12 h-12 rounded-lg bg-${iconColor}/20 flex items-center justify-center shrink-0">
        ${renderIcon(icon as Parameters<typeof renderIcon>[0], { size: 'lg', className: `text-${iconColor}` }).outerHTML}
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium text-muted-foreground truncate">${label}</p>
        <p class="text-2xl font-bold text-foreground">${value}</p>
        ${subtext ? `<p class="text-xs text-muted-foreground mt-0.5">${subtext}${subValue ? `: <span class="font-medium text-foreground">${subValue}</span>` : ''}</p>` : ''}
      </div>
    </div>
  `;

  return card;
}

/**
 * Create approval benchmarks chart
 */
function createBenchmarksChart(benchmarks: ApprovalBenchmark[], title: string): HTMLElement {
  const container = document.createElement('div');
  container.className = 'glass-card p-6 rounded-2xl';

  const header = document.createElement('div');
  header.className = 'flex items-center gap-3 mb-6';
  header.innerHTML = `
    ${renderIcon('timer', { size: 'md', className: 'text-info' }).outerHTML}
    <h3 class="text-lg font-semibold text-foreground">${escapeHtml(title)}</h3>
  `;
  container.appendChild(header);

  if (benchmarks.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'py-8 text-center text-muted-foreground';
    empty.textContent = 'No benchmark data available';
    container.appendChild(empty);
    return container;
  }

  // Create bar chart showing average, min, and max approval times
  const chartContainer = document.createElement('div');
  chartContainer.className = 'space-y-4';

  // Sort by average approval time
  benchmarks.sort((a, b) => b.averageApprovalTime - a.averageApprovalTime);

  benchmarks.forEach((benchmark, _index) => {
    const row = document.createElement('div');
    row.className = 'space-y-2';

    // Label and stats
    const labelRow = document.createElement('div');
    labelRow.className = 'flex justify-between items-center';
    labelRow.innerHTML = `
      <span class="text-sm font-medium text-foreground truncate max-w-[50%]">${escapeHtml(benchmark.dimensionValue)}</span>
      <div class="flex items-center gap-3 text-xs text-muted-foreground">
        <span>Avg: ${benchmark.averageApprovalTime.toFixed(1)}h</span>
        <span>Range: ${benchmark.minApprovalTime.toFixed(0)}-${benchmark.maxApprovalTime.toFixed(0)}h</span>
        <span class="text-foreground">${benchmark.totalRequests} requests</span>
      </div>
    `;
    row.appendChild(labelRow);

    // Range bar with min, avg, max indicators
    const barContainer = document.createElement('div');
    barContainer.className = 'relative h-6 bg-neutral-800 rounded-full overflow-visible';

    // Calculate percentages for positioning
    const maxTime = Math.max(...benchmarks.map(b => b.maxApprovalTime), 1);
    const minPercent = (benchmark.minApprovalTime / maxTime) * 100;
    const avgPercent = (benchmark.averageApprovalTime / maxTime) * 100;
    const maxPercent = (benchmark.maxApprovalTime / maxTime) * 100;

    // Range bar (from min to max)
    const rangeBar = document.createElement('div');
    rangeBar.className = 'absolute h-full bg-info/30 rounded-full';
    rangeBar.style.left = `${minPercent}%`;
    rangeBar.style.width = `${maxPercent - minPercent}%`;
    barContainer.appendChild(rangeBar);

    // Average indicator
    const avgIndicator = document.createElement('div');
    avgIndicator.className = 'absolute top-0 h-full w-1 bg-info';
    avgIndicator.style.left = `${avgPercent}%`;
    avgIndicator.title = `Average: ${benchmark.averageApprovalTime.toFixed(1)} hours`;
    barContainer.appendChild(avgIndicator);

    row.appendChild(barContainer);
    chartContainer.appendChild(row);
  });

  container.appendChild(chartContainer);
  return container;
}

/**
 * Create spending predictions chart
 */
function createPredictionsChart(predictions: SpendingPrediction[]): HTMLElement {
  const container = document.createElement('div');
  container.className = 'glass-card p-6 rounded-2xl';

  const header = document.createElement('div');
  header.className = 'flex items-center justify-between mb-6';
  header.innerHTML = `
    <div class="flex items-center gap-3">
      ${renderIcon('trendingUp', { size: 'md', className: 'text-primary' }).outerHTML}
      <h3 class="text-lg font-semibold text-foreground">Spending Predictions</h3>
    </div>
    <div class="flex items-center gap-4 text-xs">
      <div class="flex items-center gap-2">
        <div class="w-3 h-3 rounded bg-primary"></div>
        <span class="text-muted-foreground">Predicted</span>
      </div>
      <div class="flex items-center gap-2">
        <div class="w-3 h-3 rounded bg-success"></div>
        <span class="text-muted-foreground">Actual</span>
      </div>
    </div>
  `;
  container.appendChild(header);

  if (predictions.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'py-8 text-center text-muted-foreground';
    empty.textContent = 'No prediction data available';
    container.appendChild(empty);
    return container;
  }

  // Find max value for scaling
  const maxAmount = Math.max(
    ...predictions.map(p => Math.max(p.predictedAmount, p.actualAmount || 0)),
    1
  );

  // Create line chart area
  const chartArea = document.createElement('div');
  chartArea.className = 'relative h-48 mb-4';

  // SVG for line chart
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'w-full h-full');
  svg.setAttribute('viewBox', `0 0 ${predictions.length * 60} 200`);

  // Create predicted line path
  const predictedPath = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
  const predictedPoints = predictions.map((p, i) => {
    const x = i * 60 + 30;
    const y = 180 - (p.predictedAmount / maxAmount) * 160;
    return `${x},${y}`;
  }).join(' ');
  predictedPath.setAttribute('points', predictedPoints);
  predictedPath.setAttribute('fill', 'none');
  predictedPath.setAttribute('stroke', 'oklch(var(--primary))');
  predictedPath.setAttribute('stroke-width', '2');
  predictedPath.setAttribute('stroke-dasharray', predictions.some(p => !p.actualAmount) ? '5,5' : '');
  svg.appendChild(predictedPath);

  // Create actual line path (if available)
  const actualPoints = predictions
    .filter(p => p.actualAmount !== undefined)
    .map((p, i) => {
      const x = i * 60 + 30;
      const y = 180 - (p.actualAmount! / maxAmount) * 160;
      return `${x},${y}`;
    });

  if (actualPoints.length > 0) {
    const actualPath = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    actualPath.setAttribute('points', actualPoints.join(' '));
    actualPath.setAttribute('fill', 'none');
    actualPath.setAttribute('stroke', 'oklch(var(--success))');
    actualPath.setAttribute('stroke-width', '2');
    svg.appendChild(actualPath);
  }

  // Add data points
  predictions.forEach((p, i) => {
    const x = i * 60 + 30;

    // Predicted point
    const predictedY = 180 - (p.predictedAmount / maxAmount) * 160;
    const predictedCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    predictedCircle.setAttribute('cx', String(x));
    predictedCircle.setAttribute('cy', String(predictedY));
    predictedCircle.setAttribute('r', '4');
    predictedCircle.setAttribute('fill', 'oklch(var(--primary))');
    predictedCircle.setAttribute('opacity', String(p.confidence));
    svg.appendChild(predictedCircle);

    // Actual point (if available)
    if (p.actualAmount !== undefined) {
      const actualY = 180 - (p.actualAmount / maxAmount) * 160;
      const actualCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      actualCircle.setAttribute('cx', String(x));
      actualCircle.setAttribute('cy', String(actualY));
      actualCircle.setAttribute('r', '4');
      actualCircle.setAttribute('fill', 'oklch(var(--success))');
      svg.appendChild(actualCircle);
    }

    // Period label
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', String(x));
    text.setAttribute('y', '195');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('class', 'fill-muted-foreground text-xs');
    text.textContent = p.period;
    svg.appendChild(text);
  });

  chartArea.appendChild(svg);
  container.appendChild(chartArea);

  // Prediction details table
  const detailsTable = document.createElement('div');
  detailsTable.className = 'border-t border-border/50 pt-4';
  detailsTable.innerHTML = `
    <div class="overflow-x-auto">
      <table class="w-full text-xs">
        <thead>
          <tr class="border-b border-border/30">
            <th class="text-left py-2 px-2 text-muted-foreground font-medium">Period</th>
            <th class="text-right py-2 px-2 text-muted-foreground font-medium">Predicted</th>
            <th class="text-right py-2 px-2 text-muted-foreground font-medium">Actual</th>
            <th class="text-center py-2 px-2 text-muted-foreground font-medium">Trend</th>
            <th class="text-right py-2 px-2 text-muted-foreground font-medium">Change</th>
            <th class="text-right py-2 px-2 text-muted-foreground font-medium">Confidence</th>
          </tr>
        </thead>
        <tbody>
          ${predictions.map(p => {
            const trendIcon = p.trendDirection === 'up' ? 'trendingUp' :
                              p.trendDirection === 'down' ? 'trendingDown' : 'minus';
            const trendColor = p.trendDirection === 'up' ? 'text-destructive' :
                               p.trendDirection === 'down' ? 'text-success' : 'text-muted-foreground';
            const changeColor = p.percentageChange > 0 ? 'text-destructive' :
                               p.percentageChange < 0 ? 'text-success' : 'text-foreground';

            return `
              <tr class="border-b border-border/20">
                <td class="py-2 px-2 text-foreground">${escapeHtml(p.period)}</td>
                <td class="py-2 px-2 text-right text-foreground">${formatAmount(p.predictedAmount)}</td>
                <td class="py-2 px-2 text-right text-foreground">${p.actualAmount ? formatAmount(p.actualAmount) : '-'}</td>
                <td class="py-2 px-2 text-center ${trendColor}">
                  ${renderIcon(trendIcon as any, { size: 'xs' }).outerHTML}
                </td>
                <td class="py-2 px-2 text-right ${changeColor}">
                  ${p.percentageChange > 0 ? '+' : ''}${p.percentageChange.toFixed(1)}%
                </td>
                <td class="py-2 px-2 text-right text-muted-foreground">${(p.confidence * 100).toFixed(0)}%</td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
  `;
  container.appendChild(detailsTable);

  return container;
}

/**
 * Create comparison indicators
 */
function createComparisonIndicator(
  label: string,
  currentValue: number | string,
  percentageChange: number,
  _formatter?: (val: number) => string
): HTMLElement {
  const container = document.createElement('div');
  container.className = 'glass-sm p-4 rounded-xl';

  const isPositive = percentageChange > 0;
  const changeColor = isPositive ? 'text-destructive' : 'text-success';
  const changeIcon = isPositive ? 'trendingUp' : 'trendingDown';
  const changePrefix = isPositive ? '+' : '';

  container.innerHTML = `
    <div class="flex items-center justify-between">
      <div class="flex-1">
        <p class="text-xs text-muted-foreground mb-1">${escapeHtml(label)}</p>
        <p class="text-xl font-bold text-foreground">${currentValue}</p>
      </div>
      <div class="flex items-center gap-1 ${changeColor}">
        ${renderIcon(changeIcon as any, { size: 'sm' }).outerHTML}
        <span class="text-sm font-medium">${changePrefix}${percentageChange.toFixed(1)}%</span>
      </div>
    </div>
    <p class="text-xs text-muted-foreground mt-2">vs previous period</p>
  `;

  return container;
}

/**
 * Create the aging table
 */
function createAgingTable(agingData: AgingReport): HTMLElement {
  const container = document.createElement('div');
  container.className = 'glass-card p-6 rounded-2xl';

  const header = document.createElement('div');
  header.className = 'flex items-center justify-between mb-6';
  header.innerHTML = `
    <div class="flex items-center gap-3">
      ${renderIcon('clock', { size: 'md', className: 'text-warning' }).outerHTML}
      <h3 class="text-lg font-semibold text-foreground">Pending Approval Aging</h3>
    </div>
    <div class="text-right">
      <p class="text-sm text-muted-foreground">Average age</p>
      <p class="text-xl font-bold text-foreground">${agingData.summary.averageDaysAging} days</p>
    </div>
  `;
  container.appendChild(header);

  // Summary stats
  const summary = document.createElement('div');
  summary.className = 'grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-4 bg-neutral-900/50 rounded-lg';
  summary.innerHTML = `
    <div>
      <p class="text-xs text-muted-foreground">Total Pending</p>
      <p class="text-lg font-semibold text-foreground">${agingData.summary.totalPending}</p>
    </div>
    <div>
      <p class="text-xs text-muted-foreground">Total Amount</p>
      <p class="text-lg font-semibold text-foreground">${formatAmount(agingData.summary.totalAmount)}</p>
    </div>
    <div>
      <p class="text-xs text-muted-foreground">Avg. Days Aging</p>
      <p class="text-lg font-semibold text-warning">${agingData.summary.averageDaysAging}</p>
    </div>
    <div>
      <p class="text-xs text-muted-foreground">Oldest Request</p>
      <p class="text-lg font-semibold text-destructive">${agingData.summary.oldestRequest} days</p>
    </div>
  `;
  container.appendChild(summary);

  // Buckets table
  const table = document.createElement('div');
  table.className = 'overflow-x-auto';

  const tableHtml = `
    <table class="w-full text-sm">
      <thead>
        <tr class="border-b border-border/50">
          <th class="text-left py-3 px-4 text-muted-foreground font-medium">Age Range</th>
          <th class="text-right py-3 px-4 text-muted-foreground font-medium">Count</th>
          <th class="text-right py-3 px-4 text-muted-foreground font-medium">Amount</th>
          <th class="text-right py-3 px-4 text-muted-foreground font-medium">% of Total</th>
        </tr>
      </thead>
      <tbody>
        ${agingData.buckets
          .map((bucket) => {
            const percentage =
              agingData.summary.totalPending > 0
                ? (bucket.count / agingData.summary.totalPending) * 100
                : 0;
            const isHighRisk = bucket.minDays >= 15;
            const rowClass = isHighRisk ? 'bg-warning/5' : '';
            return `
            <tr class="border-b border-border/30 hover:bg-muted/30 transition-colors ${rowClass}">
              <td class="py-3 px-4 font-medium ${isHighRisk ? 'text-warning' : 'text-foreground'}">${escapeHtml(bucket.bucket)}</td>
              <td class="py-3 px-4 text-right text-foreground">${bucket.count}</td>
              <td class="py-3 px-4 text-right text-foreground">${formatAmount(bucket.totalAmount)}</td>
              <td class="py-3 px-4 text-right text-muted-foreground">${percentage.toFixed(1)}%</td>
            </tr>
          `;
          })
          .join('')}
      </tbody>
    </table>
  `;
  table.innerHTML = tableHtml;
  container.appendChild(table);

  return container;
}

/**
 * Create loading placeholder
 */
function createLoadingPlaceholder(className: string = ''): HTMLElement {
  const placeholder = document.createElement('div');
  placeholder.className = `glass-card p-6 rounded-2xl animate-pulse ${className}`;
  placeholder.innerHTML = `
    <div class="flex items-center gap-3 mb-6">
      <div class="w-8 h-8 rounded bg-muted"></div>
      <div class="h-6 bg-muted rounded w-40"></div>
    </div>
    <div class="space-y-4">
      <div class="h-4 bg-muted rounded w-full"></div>
      <div class="h-4 bg-muted rounded w-3/4"></div>
      <div class="h-4 bg-muted rounded w-1/2"></div>
    </div>
  `;
  return placeholder;
}

/**
 * Create the reports dashboard view
 */
export function createReportsView(): HTMLElement {
  // State
  const state: DateRangeState = {
    dateRange: {
      fromDate: '',
      toDate: '',
      preset: 'ytd'
    }
  };

  // Track Chart.js instances for proper cleanup
  const chartInstances: ChartInstances = {};

  // Initialize with YTD dates (January 1st to today)
  const now = new Date();
  const year = now.getFullYear();
  state.dateRange.fromDate = `${year}-01-01`;
  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  state.dateRange.toDate = formatDate(now);

  // Main content
  const main = document.createElement('div');
  main.className = 'px-4 lg:px-6 py-6';

  // Page description
  const titleSection = document.createElement('div');
  titleSection.className = 'mb-8';
  titleSection.innerHTML = `
    <p class="text-muted-foreground">Analytics and insights for expenditure requests</p>
  `;
  main.appendChild(titleSection);

  // Date range filter section
  const filterSection = document.createElement('div');
  filterSection.className = 'glass-card p-4 rounded-xl mb-8';

  const filterContent = document.createElement('div');
  filterContent.className = 'flex flex-wrap items-end gap-4';

  // Date range picker
  const dateRangeWrapper = document.createElement('div');
  dateRangeWrapper.className = 'flex-1 min-w-[400px]';
  const dateRangePicker = createDateRangePicker({
    id: 'date-range',
    value: state.dateRange,
    onChange: (value) => {
      state.dateRange = value;
      loadAllData();
    },
  });
  dateRangeWrapper.appendChild(dateRangePicker);
  filterContent.appendChild(dateRangeWrapper);

  // Interval selector for trends
  const intervalWrapper = document.createElement('div');
  intervalWrapper.className = 'flex flex-col gap-1.5';
  intervalWrapper.innerHTML = `<label class="text-sm font-medium text-muted-foreground">Trend Interval</label>`;
  const intervalSelect = createSelect({
    id: 'interval',
    name: 'interval',
    options: [
      { value: 'monthly', label: 'Monthly' },
      { value: 'quarterly', label: 'Quarterly' },
    ],
    value: 'monthly',
  });
  intervalSelect.addEventListener('change', () => loadTrends());
  intervalWrapper.appendChild(intervalSelect);
  filterContent.appendChild(intervalWrapper);

  // Spacer
  const spacer = document.createElement('div');
  spacer.className = 'flex-grow';
  filterContent.appendChild(spacer);

  // Export buttons container
  const exportContainer = document.createElement('div');
  exportContainer.className = 'flex items-center gap-2';

  // Export CSV button
  const exportCsvButton = document.createElement('button');
  exportCsvButton.className = 'px-4 py-2 glass-button font-medium rounded-lg text-foreground flex items-center gap-2 hover:bg-success/10 hover:text-success transition-colors';
  exportCsvButton.innerHTML = `
    ${renderIcon('download', { size: 'sm' }).outerHTML}
    <span>Export CSV</span>
  `;
  exportCsvButton.addEventListener('click', () => exportReport('csv'));
  exportContainer.appendChild(exportCsvButton);

  // Export PDF button
  const exportPdfButton = document.createElement('button');
  exportPdfButton.className = 'px-4 py-2 glass-button font-medium rounded-lg text-foreground flex items-center gap-2 hover:bg-primary/10 hover:text-primary transition-colors';
  exportPdfButton.innerHTML = `
    ${renderIcon('fileText', { size: 'sm' }).outerHTML}
    <span>Export PDF</span>
  `;
  exportPdfButton.addEventListener('click', () => exportReport('pdf'));
  exportContainer.appendChild(exportPdfButton);

  filterContent.appendChild(exportContainer);

  filterSection.appendChild(filterContent);
  main.appendChild(filterSection);

  // Comparison indicators section (period-over-period)
  const comparisonSection = document.createElement('div');
  comparisonSection.id = 'comparison-section';
  comparisonSection.className = 'grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8';
  // Add loading placeholders
  for (let i = 0; i < 3; i++) {
    const placeholder = document.createElement('div');
    placeholder.className = 'glass-sm p-4 rounded-xl animate-pulse';
    placeholder.innerHTML = `
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <div class="h-3 bg-muted rounded w-20 mb-2"></div>
          <div class="h-6 bg-muted rounded w-24"></div>
        </div>
        <div class="w-16 h-8 bg-muted rounded"></div>
      </div>
    `;
    comparisonSection.appendChild(placeholder);
  }
  main.appendChild(comparisonSection);

  // Summary stats section
  const statsSection = document.createElement('div');
  statsSection.id = 'stats-section';
  statsSection.className = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8';
  // Add loading placeholders
  for (let i = 0; i < 4; i++) {
    const placeholder = document.createElement('div');
    placeholder.className = 'glass-sm p-5 rounded-xl animate-pulse';
    placeholder.innerHTML = `
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 rounded-lg bg-muted"></div>
        <div class="flex-1">
          <div class="h-4 bg-muted rounded w-24 mb-2"></div>
          <div class="h-8 bg-muted rounded w-16"></div>
        </div>
      </div>
    `;
    statsSection.appendChild(placeholder);
  }
  main.appendChild(statsSection);

  // Charts grid - top row with status distribution and category breakdown
  const chartsGridTop = document.createElement('div');
  chartsGridTop.className = 'grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8';

  // Status distribution doughnut chart
  const statusDistContainer = document.createElement('div');
  statusDistContainer.id = 'status-distribution-chart';
  statusDistContainer.appendChild(createLoadingPlaceholder());
  chartsGridTop.appendChild(statusDistContainer);

  // By type doughnut chart
  const byTypeContainer = document.createElement('div');
  byTypeContainer.id = 'by-type-chart';
  byTypeContainer.appendChild(createLoadingPlaceholder());
  chartsGridTop.appendChild(byTypeContainer);

  // Approval rate metrics
  const approvalRateContainer = document.createElement('div');
  approvalRateContainer.id = 'approval-rate-chart';
  approvalRateContainer.appendChild(createLoadingPlaceholder());
  chartsGridTop.appendChild(approvalRateContainer);

  main.appendChild(chartsGridTop);

  // Department comparison bar chart (full width)
  const byDeptContainer = document.createElement('div');
  byDeptContainer.id = 'by-dept-chart';
  byDeptContainer.className = 'mb-8';
  byDeptContainer.appendChild(createLoadingPlaceholder());
  main.appendChild(byDeptContainer);

  // Advanced Analytics Grid (Benchmarks and Predictions)
  const analyticsGrid = document.createElement('div');
  analyticsGrid.className = 'grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8';

  // Benchmarks chart placeholder
  const benchmarksContainer = document.createElement('div');
  benchmarksContainer.id = 'benchmarks-chart';
  benchmarksContainer.appendChild(createLoadingPlaceholder());
  analyticsGrid.appendChild(benchmarksContainer);

  // Predictions chart placeholder
  const predictionsContainer = document.createElement('div');
  predictionsContainer.id = 'predictions-chart';
  predictionsContainer.appendChild(createLoadingPlaceholder());
  analyticsGrid.appendChild(predictionsContainer);

  main.appendChild(analyticsGrid);

  // Trends chart (full width)
  const trendsContainer = document.createElement('div');
  trendsContainer.id = 'trends-chart';
  trendsContainer.className = 'mb-8';
  trendsContainer.appendChild(createLoadingPlaceholder());
  main.appendChild(trendsContainer);

  // Aging table
  const agingContainer = document.createElement('div');
  agingContainer.id = 'aging-table';
  agingContainer.className = 'mb-8';
  agingContainer.appendChild(createLoadingPlaceholder());
  main.appendChild(agingContainer);

  // Note: main is the content element that will be wrapped with sidebar layout

  /**
   * Build query params from state
   */
  function buildQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (state.dateRange.fromDate) params.fromDate = state.dateRange.fromDate;
    if (state.dateRange.toDate) params.toDate = state.dateRange.toDate;
    return params;
  }

  /**
   * Load summary statistics
   * Uses the statsSection element directly instead of document.getElementById to handle
   * the case where the element hasn't been attached to the DOM yet
   */
  async function loadSummaryStats(): Promise<void> {
    const section = statsSection;
    if (!section) return;

    try {
      const response = await apiClient.get<{ data: SummaryStats }>('/reports/summary', {
        params: buildQueryParams(),
      });
      const stats = response.data.data;

      section.innerHTML = '';

      // Total requests card
      section.appendChild(
        createStatCard(
          'fileText',
          'primary',
          'Total Requests',
          formatNumber(stats.totals.totalRequests),
          'Total Amount',
          formatAmount(stats.totals.totalAmount)
        )
      );

      // Average amount card
      section.appendChild(
        createStatCard(
          'calculator',
          'info',
          'Average Amount',
          formatAmount(stats.totals.averageAmount),
          `Per request`
        )
      );

      // By status cards
      stats.byStatus.forEach((statusData) => {
        const style = statusColors[statusData.status] || {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: 'info',
        };
        section.appendChild(
          createStatCard(
            style.icon,
            style.text.replace('text-', ''),
            statusData.status.charAt(0) + statusData.status.slice(1).toLowerCase(),
            formatNumber(statusData.count),
            'Amount',
            formatAmount(statusData.totalAmount)
          )
        );
      });

      // Also update the status distribution doughnut chart
      updateStatusDistributionChart(stats);
    } catch (error) {
      console.error('Failed to load summary stats:', error);
      section.innerHTML = `
        <div class="col-span-full glass-sm p-6 rounded-xl text-center">
          <p class="text-muted-foreground mb-2">Failed to load summary statistics</p>
          <button onclick="window.location.reload()" class="text-primary hover:text-primary/80 text-sm">
            Refresh page
          </button>
        </div>
      `;
    }
  }

  /**
   * Update the status distribution doughnut chart
   */
  function updateStatusDistributionChart(stats: SummaryStats): void {
    const container = statusDistContainer;
    if (!container) return;

    // Destroy existing chart if any
    if (chartInstances.statusDistribution) {
      chartInstances.statusDistribution.destroy();
    }

    container.innerHTML = '';

    // Create card wrapper
    const card = document.createElement('div');
    card.className = 'glass-card p-6 rounded-2xl h-full';

    // Header
    const header = document.createElement('div');
    header.className = 'flex items-center gap-3 mb-4';
    header.innerHTML = `
      ${renderIcon('pieChart', { size: 'md', className: 'text-primary' }).outerHTML}
      <h3 class="text-lg font-semibold text-foreground">Request Status</h3>
    `;
    card.appendChild(header);

    if (stats.byStatus.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'py-8 text-center text-muted-foreground';
      empty.textContent = 'No data available';
      card.appendChild(empty);
      container.appendChild(card);
      return;
    }

    // Create doughnut chart
    const labels = stats.byStatus.map((s) =>
      s.status.charAt(0) + s.status.slice(1).toLowerCase()
    );
    const data = stats.byStatus.map((s) => s.count);

    chartInstances.statusDistribution = createDoughnutChart(labels, data, {
      cutout: '65%',
      formatter: (v) => `${v} requests`,
    });

    card.appendChild(chartInstances.statusDistribution.element);
    container.appendChild(card);
  }

  /**
   * Load expenditure by type
   * Uses byTypeContainer directly instead of document.getElementById
   */
  async function loadByType(): Promise<void> {
    const container = byTypeContainer;
    if (!container) return;

    try {
      const response = await apiClient.get<{ data: ExpenditureByType[] }>('/reports/by-type', {
        params: buildQueryParams(),
      });
      const data = response.data.data;

      // Destroy existing chart if any
      if (chartInstances.byType) {
        chartInstances.byType.destroy();
      }

      container.innerHTML = '';

      // Create card wrapper
      const card = document.createElement('div');
      card.className = 'glass-card p-6 rounded-2xl h-full';

      // Header
      const header = document.createElement('div');
      header.className = 'flex items-center gap-3 mb-4';
      header.innerHTML = `
        ${renderIcon('barChart', { size: 'md', className: 'text-info' }).outerHTML}
        <h3 class="text-lg font-semibold text-foreground">By Category</h3>
      `;
      card.appendChild(header);

      if (data.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'py-8 text-center text-muted-foreground';
        empty.textContent = 'No data available';
        card.appendChild(empty);
        container.appendChild(card);
        return;
      }

      // Create doughnut chart for category breakdown
      const labels = data.map((item) => item.type);
      const chartData = data.map((item) => item.totalAmount);

      chartInstances.byType = createDoughnutChart(labels, chartData, {
        cutout: '65%',
        formatter: (v) => formatAmount(v),
      });

      card.appendChild(chartInstances.byType.element);
      container.appendChild(card);
    } catch (error) {
      console.error('Failed to load by-type data:', error);
      container.innerHTML = `
        <div class="glass-card p-6 rounded-2xl text-center">
          <p class="text-muted-foreground">Failed to load expenditure by type data</p>
        </div>
      `;
    }
  }

  /**
   * Load expenditure by department
   * Uses byDeptContainer directly instead of document.getElementById
   */
  async function loadByDepartment(): Promise<void> {
    const container = byDeptContainer;
    if (!container) return;

    try {
      const response = await apiClient.get<{ data: ExpenditureByDepartment[] }>(
        '/reports/by-department',
        {
          params: buildQueryParams(),
        }
      );
      const data = response.data.data;

      // Destroy existing chart if any
      if (chartInstances.byDept) {
        chartInstances.byDept.destroy();
      }

      container.innerHTML = '';

      // Create card wrapper
      const card = document.createElement('div');
      card.className = 'glass-card p-6 rounded-2xl';

      // Header
      const header = document.createElement('div');
      header.className = 'flex items-center gap-3 mb-4';
      header.innerHTML = `
        ${renderIcon('building', { size: 'md', className: 'text-success' }).outerHTML}
        <h3 class="text-lg font-semibold text-foreground">Expenditure by Department</h3>
      `;
      card.appendChild(header);

      if (data.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'py-8 text-center text-muted-foreground';
        empty.textContent = 'No data available';
        card.appendChild(empty);
        container.appendChild(card);
        return;
      }

      // Create horizontal bar chart for department comparison
      const labels = data.map((item) => item.department);
      const chartData = data.map((item) => item.totalAmount);

      chartInstances.byDept = createHorizontalBarChart(
        labels,
        [{ label: 'Total Amount', data: chartData }],
        { yAxisFormatter: (v) => formatAmount(v) }
      );

      card.appendChild(chartInstances.byDept.element);
      container.appendChild(card);
    } catch (error) {
      console.error('Failed to load by-department data:', error);
      container.innerHTML = `
        <div class="glass-card p-6 rounded-2xl text-center">
          <p class="text-muted-foreground">Failed to load expenditure by department data</p>
        </div>
      `;
    }
  }

  /**
   * Load trends data
   * Uses trendsContainer directly instead of document.getElementById
   */
  async function loadTrends(): Promise<void> {
    const container = trendsContainer;
    if (!container) return;

    try {
      const interval = intervalSelect.value || 'monthly';
      const response = await apiClient.get<{ data: TrendDataPoint[]; interval: string }>(
        '/reports/trends',
        {
          params: { ...buildQueryParams(), interval },
        }
      );
      const { data, interval: responseInterval } = response.data;

      // Destroy existing chart if any
      if (chartInstances.trends) {
        chartInstances.trends.destroy();
      }

      container.innerHTML = '';

      // Create card wrapper
      const card = document.createElement('div');
      card.className = 'glass-card p-6 rounded-2xl';

      // Header
      const header = document.createElement('div');
      header.className = 'flex items-center justify-between mb-4';
      header.innerHTML = `
        <div class="flex items-center gap-3">
          ${renderIcon('trendingUp', { size: 'md', className: 'text-primary' }).outerHTML}
          <h3 class="text-lg font-semibold text-foreground">Spending Trends</h3>
        </div>
        <span class="text-xs text-muted-foreground uppercase tracking-wider px-2 py-1 bg-muted/30 rounded">
          ${responseInterval}
        </span>
      `;
      card.appendChild(header);

      if (data.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'py-8 text-center text-muted-foreground';
        empty.textContent = 'No trend data available';
        card.appendChild(empty);
        container.appendChild(card);
        return;
      }

      // Create line chart for spending trends
      const labels = data.map((d) => d.period);

      chartInstances.trends = createLineChart(
        labels,
        [
          {
            label: 'Total Amount',
            data: data.map((d) => d.totalAmount),
            color: 'oklch(0.67 0.18 281)', // primary
            fill: true,
          },
          {
            label: 'Approved',
            data: data.map((d) => d.approvedAmount),
            color: 'oklch(0.72 0.17 142)', // success
          },
          {
            label: 'Rejected',
            data: data.map((d) => d.rejectedAmount),
            color: 'oklch(0.63 0.24 29)', // destructive
          },
        ],
        { yAxisFormatter: (v) => formatAmount(v), tension: 0.3 }
      );

      card.appendChild(chartInstances.trends.element);
      container.appendChild(card);
    } catch (error) {
      console.error('Failed to load trends data:', error);
      container.innerHTML = `
        <div class="glass-card p-6 rounded-2xl text-center">
          <p class="text-muted-foreground">Failed to load trends data</p>
        </div>
      `;
    }
  }

  /**
   * Load aging data
   * Uses agingContainer directly instead of document.getElementById
   */
  async function loadAging(): Promise<void> {
    const container = agingContainer;
    if (!container) return;

    try {
      const response = await apiClient.get<{ data: AgingReport }>('/reports/pending-aging');
      const data = response.data.data;

      container.innerHTML = '';
      container.appendChild(createAgingTable(data));
    } catch (error) {
      console.error('Failed to load aging data:', error);
      container.innerHTML = `
        <div class="glass-card p-6 rounded-2xl text-center">
          <p class="text-muted-foreground">Failed to load aging data</p>
        </div>
      `;
    }
  }

  /**
   * Load comparison data (period-over-period)
   */
  async function loadComparison(): Promise<void> {
    const section = comparisonSection;
    if (!section) return;

    try {
      // Calculate previous period based on date range
      const fromDate = new Date(state.dateRange.fromDate);
      const toDate = new Date(state.dateRange.toDate);
      const daysDiff = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));

      const prevToDate = new Date(fromDate.getTime() - 1);
      const prevFromDate = new Date(prevToDate.getTime() - (daysDiff * 1000 * 60 * 60 * 24));

      // Format dates for API
      const formatDate = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };

      // Fetch current and previous period data
      const [currentResponse, previousResponse] = await Promise.all([
        apiClient.get<{ data: SummaryStats }>('/reports/summary', {
          params: buildQueryParams(),
        }),
        apiClient.get<{ data: SummaryStats }>('/reports/summary', {
          params: {
            fromDate: formatDate(prevFromDate),
            toDate: formatDate(prevToDate),
          },
        }),
      ]);

      const currentStats = currentResponse.data.data;
      const previousStats = previousResponse.data.data;

      // Calculate percentage changes
      const amountChange = previousStats.totals.totalAmount > 0
        ? ((currentStats.totals.totalAmount - previousStats.totals.totalAmount) / previousStats.totals.totalAmount) * 100
        : 0;
      const countChange = previousStats.totals.totalRequests > 0
        ? ((currentStats.totals.totalRequests - previousStats.totals.totalRequests) / previousStats.totals.totalRequests) * 100
        : 0;
      const avgChange = previousStats.totals.averageAmount > 0
        ? ((currentStats.totals.averageAmount - previousStats.totals.averageAmount) / previousStats.totals.averageAmount) * 100
        : 0;

      section.innerHTML = '';

      // Total spending indicator
      section.appendChild(
        createComparisonIndicator(
          'Total Spending',
          formatAmount(currentStats.totals.totalAmount),
          amountChange
        )
      );

      // Request count indicator
      section.appendChild(
        createComparisonIndicator(
          'Total Requests',
          formatNumber(currentStats.totals.totalRequests),
          countChange
        )
      );

      // Average amount indicator
      section.appendChild(
        createComparisonIndicator(
          'Average Request',
          formatAmount(currentStats.totals.averageAmount),
          avgChange
        )
      );
    } catch (error) {
      console.error('Failed to load comparison data:', error);
      section.innerHTML = `
        <div class="col-span-full glass-sm p-4 rounded-xl text-center">
          <p class="text-muted-foreground">Failed to load comparison data</p>
        </div>
      `;
    }
  }

  /**
   * Load approval benchmarks and approval rate metrics
   */
  async function loadBenchmarks(): Promise<void> {
    const container = benchmarksContainer;
    if (!container) return;

    try {
      const response = await apiClient.get<{
        data: {
          overall: ApprovalBenchmark;
          byDepartment: ApprovalBenchmark[];
          byCategory: ApprovalBenchmark[];
        }
      }>('/reports/approval-benchmarks', {
        params: buildQueryParams(),
      });
      const data = response.data.data;

      container.innerHTML = '';
      // Show by department benchmarks by default, could add toggle for by category
      container.appendChild(
        createBenchmarksChart(data.byDepartment, 'Approval Time Benchmarks by Department')
      );

      // Also update approval rate metrics chart
      updateApprovalRateChart(data);
    } catch (error) {
      console.error('Failed to load benchmarks data:', error);
      container.innerHTML = `
        <div class="glass-card p-6 rounded-2xl text-center">
          <p class="text-muted-foreground">Failed to load benchmarks data</p>
        </div>
      `;
    }
  }

  /**
   * Update the approval rate metrics chart
   */
  function updateApprovalRateChart(data: {
    overall: ApprovalBenchmark;
    byDepartment: ApprovalBenchmark[];
    byCategory: ApprovalBenchmark[];
  }): void {
    const container = approvalRateContainer;
    if (!container) return;

    // Destroy existing chart if any
    if (chartInstances.approvalRate) {
      chartInstances.approvalRate.destroy();
    }

    container.innerHTML = '';

    // Create card wrapper
    const card = document.createElement('div');
    card.className = 'glass-card p-6 rounded-2xl h-full';

    // Header
    const header = document.createElement('div');
    header.className = 'flex items-center gap-3 mb-4';
    header.innerHTML = `
      ${renderIcon('checkCircle', { size: 'md', className: 'text-success' }).outerHTML}
      <h3 class="text-lg font-semibold text-foreground">Approval Metrics</h3>
    `;
    card.appendChild(header);

    // Overall approval rate display
    const overall = data.overall;
    if (overall && overall.totalRequests > 0) {
      // Large approval rate indicator
      const rateDisplay = document.createElement('div');
      rateDisplay.className = 'text-center mb-4';
      rateDisplay.innerHTML = `
        <div class="text-4xl font-bold text-success mb-1">${overall.approvalRate.toFixed(1)}%</div>
        <div class="text-sm text-muted-foreground">Overall Approval Rate</div>
      `;
      card.appendChild(rateDisplay);

      // Stats grid
      const statsGrid = document.createElement('div');
      statsGrid.className = 'grid grid-cols-2 gap-3 text-sm';
      statsGrid.innerHTML = `
        <div class="glass-sm p-3 rounded-lg text-center">
          <div class="text-lg font-semibold text-success">${overall.approvedRequests}</div>
          <div class="text-xs text-muted-foreground">Approved</div>
        </div>
        <div class="glass-sm p-3 rounded-lg text-center">
          <div class="text-lg font-semibold text-destructive">${overall.rejectedRequests}</div>
          <div class="text-xs text-muted-foreground">Rejected</div>
        </div>
        <div class="glass-sm p-3 rounded-lg text-center col-span-2">
          <div class="text-lg font-semibold text-foreground">${overall.averageApprovalTime.toFixed(1)}h</div>
          <div class="text-xs text-muted-foreground">Avg. Approval Time</div>
        </div>
      `;
      card.appendChild(statsGrid);
    } else {
      const empty = document.createElement('div');
      empty.className = 'py-8 text-center text-muted-foreground';
      empty.textContent = 'No approval data available';
      card.appendChild(empty);
    }

    container.appendChild(card);
  }

  /**
   * Load spending predictions
   */
  async function loadPredictions(): Promise<void> {
    const container = predictionsContainer;
    if (!container) return;

    try {
      const response = await apiClient.get<{
        data: {
          predictions: SpendingPrediction[];
        }
      }>('/reports/spending-predictions', {
        params: buildQueryParams(),
      });
      const data = response.data.data;

      container.innerHTML = '';
      container.appendChild(createPredictionsChart(data.predictions));
    } catch (error) {
      console.error('Failed to load predictions data:', error);
      container.innerHTML = `
        <div class="glass-card p-6 rounded-2xl text-center">
          <p class="text-muted-foreground">Failed to load predictions data</p>
        </div>
      `;
    }
  }

  /**
   * Load all data
   */
  async function loadAllData(): Promise<void> {
    await Promise.all([
      loadComparison(),
      loadSummaryStats(),
      loadByType(),
      loadByDepartment(),
      loadBenchmarks(),
      loadPredictions(),
      loadTrends(),
      loadAging()
    ]);
  }

  /**
   * Export report data to CSV or PDF
   */
  function exportReport(format: 'csv' | 'pdf'): void {
    // Build export URL with current filters
    const params = new URLSearchParams();
    params.set('format', format);
    if (state.dateRange.fromDate) params.set('fromDate', state.dateRange.fromDate);
    if (state.dateRange.toDate) params.set('toDate', state.dateRange.toDate);

    // Open in new window/tab for download (authenticated endpoint uses session cookie)
    const exportUrl = `/api/reports/export?${params.toString()}`;
    window.open(exportUrl, '_blank');
  }

  // Initial data load
  loadAllData();

  // Wrap with sidebar layout
  return createSidebarLayout(main, {
    currentPath: '/reports',
    title: 'Reports',
    subtitle: 'Analytics and insights',
  });
}

/**
 * Reports view function for router
 */
export function reportsView(): HTMLElement {
  return createReportsView();
}

export default reportsView;
